import type {
  AIBasicModelMessage,
  AIConfig,
  AIContext,
  AIContextSettings,
  AIProvider,
  ChatMessage,
} from '@tunasong/ai-lib'
import { aiConfigSchema } from '@tunasong/ai-lib'
import { AIConfigForm, AIConfigPreview } from '@tunasong/ai-ui'
import { graphHooks } from '@tunasong/graph-lib/react'
import type { LayoutComponent } from '@tunasong/plugin-lib'
import { nlpApi, skipToken, useThunkDispatch } from '@tunasong/redux'
import type { BandSpace } from '@tunasong/schemas'
import { isAudio, isBandSpace, isImage, isPersisted, isStorageUrlsExpired } from '@tunasong/schemas'
import { Typography, useImage, usePreviewGeneration } from '@tunasong/ui-lib'
import type { ModelMessage } from 'ai'
import type { FC } from 'react'
import { useState } from 'react'
import styles from './style.module.css'

export interface StyleViewProps extends LayoutComponent<BandSpace> {
  className?: string
}

interface StreamParams {
  prompt: ModelMessage[]
  model: string
  providerName: string
  requestId: string
}

const StyleView: FC<StyleViewProps> = props => {
  const { entity } = props

  const updateEntity = graphHooks.useEntityUpdate<BandSpace>({ debounceDelay: 1000 })
  const dispatch = useThunkDispatch()

  // Text streaming state
  const [streamParams, setStreamParams] = useState<StreamParams | null>(null)
  const { currentData: textResult } = nlpApi.useCompleteStreamQuery(streamParams ?? skipToken)

  const imagePreview = usePreviewGeneration('image preview')
  const imageUrl = useImage(isImage(imagePreview.entity) ? imagePreview.entity : undefined, 'medium')

  const musicPreview = usePreviewGeneration('music preview')
  const musicEntity = musicPreview.entity
  const musicUrl =
    isAudio(musicEntity) && !isStorageUrlsExpired(musicEntity.storageUrls) ? musicEntity.storageUrls?.url : undefined

  const handleChange = (config: AIConfig) => {
    if (isPersisted(entity)) {
      updateEntity(entity.id, { aiConfig: config })
    }
  }

  const handleGenerateText = (params: {
    provider: AIProvider
    model: string
    prompt: ModelMessage[]
    requestId: string
  }) =>
    setStreamParams({
      prompt: params.prompt,
      model: params.model,
      providerName: params.provider,
      requestId: params.requestId,
    })

  const handleGenerateImage = async (params: { description: string; model: string }) => {
    if (!isPersisted(entity)) {
      return
    }
    const prompt: ChatMessage[] = [{ role: 'user', content: params.description }]
    await imagePreview.generate(() =>
      dispatch(
        nlpApi.endpoints.image.initiate({
          prompt,
          chatModel: params.model,
          parentId: entity.id,
          name: 'Band Style Preview',
          size: '1024x1024',
        })
      ).unwrap()
    )
  }

  const handleGenerateMusic = async (params: { description: string; model: string }) => {
    if (!isPersisted(entity)) {
      return
    }
    const prompt: AIBasicModelMessage[] = [{ role: 'user', content: params.description }]
    await musicPreview.generate(() =>
      dispatch(
        nlpApi.endpoints.generateMusic.initiate({
          prompt,
          textModel: params.model,
          parentId: entity.id,
          name: 'Band Music Style Preview',
          durationSec: 15,
          instrumental: true,
        })
      ).unwrap()
    )
  }

  // Clear preview state when context changes
  const handleContextChange = () => {
    setStreamParams(null)
    imagePreview.reset()
    musicPreview.reset()
  }

  const renderPreview = ({ context, settings }: { context: AIContext; settings: AIContextSettings }) => (
    <AIConfigPreview
      key={context}
      context={context}
      settings={settings}
      textResult={streamParams ? textResult : undefined}
      imageUrl={imageUrl}
      imagePending={imagePreview.pending}
      musicUrl={musicUrl}
      musicPending={musicPreview.pending}
      onGenerateText={handleGenerateText}
      onGenerateImage={handleGenerateImage}
      onGenerateMusic={handleGenerateMusic}
      onContextChange={handleContextChange}
    />
  )

  if (!(isBandSpace(entity) && isPersisted(entity))) {
    return null
  }

  const aiConfig = aiConfigSchema.safeParse(entity.aiConfig)

  return (
    <div className={styles.container}>
      <Typography variant="h6" gutterBottom>
        Band Style
      </Typography>
      <Typography variant="body2" color="text.secondary" className={styles.subtitle}>
        Configure AI generation styles for this band. These settings are automatically applied when generating lyrics,
        images, or music clips for songs in this bandspace.
      </Typography>
      <AIConfigForm
        key={entity.id}
        defaultValue={aiConfig.data}
        onChange={handleChange}
        renderPreview={renderPreview}
      />
    </div>
  )
}

export default StyleView
